import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function AreasCoveredPage() {
  useSEO({
    title: 'Service Areas - Solar Panel Cleaning, Window Washing & Bird Proofing Sacramento | Mr. EcoClean',
    description: 'Mr. EcoClean provides professional solar panel cleaning, window washing, and bird proofing services throughout Sacramento and surrounding areas including Elk Grove, Roseville, Folsom, Citrus Heights, and more. Find your local service area.',
    keywords: 'solar panel cleaning service areas Sacramento, window washing near me, bird proofing Sacramento, local solar cleaning, Sacramento County solar services',
    canonical: '/areas-covered',
    ogType: 'website'
  });

  const serviceAreas = [
    { name: 'Sacramento', slug: 'sacramento', zip: '95814' },
    { name: 'Elk Grove', slug: 'elk-grove', zip: '95624' },
    { name: 'Roseville', slug: 'roseville', zip: '95678' },
    { name: 'Folsom', slug: 'folsom', zip: '95630' },
    { name: 'Citrus Heights', slug: 'citrus-heights', zip: '95610' },
    { name: 'Rancho Cordova', slug: 'rancho-cordova', zip: '95670' },
    { name: 'Rocklin', slug: 'rocklin', zip: '95677' },
    { name: 'Davis', slug: 'davis', zip: '95616' },
    { name: 'West Sacramento', slug: 'west-sacramento', zip: '95691' },
    { name: 'Carmichael', slug: 'carmichael', zip: '95608' },
    { name: 'Fair Oaks', slug: 'fair-oaks', zip: '95628' },
    { name: 'Orangevale', slug: 'orangevale', zip: '95662' },
    { name: 'North Highlands', slug: 'north-highlands', zip: '95660' },
    { name: 'Antelope', slug: 'antelope', zip: '95843' },
    { name: 'Lincoln', slug: 'lincoln', zip: '95648' },
    { name: 'Auburn', slug: 'auburn', zip: '95603' },
    { name: 'Granite Bay', slug: 'granite-bay', zip: '95746' },
    { name: 'El Dorado Hills', slug: 'el-dorado-hills', zip: '95762' },
    { name: 'Natomas', slug: 'natomas', zip: '95833' },
    { name: 'Land Park', slug: 'land-park', zip: '95818' },
    { name: 'Arden-Arcade', slug: 'arden-arcade', zip: '95825' },
    { name: 'Placerville', slug: 'placerville', zip: '95667' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  Mr. EcoClean
                </h1>
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/services" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/areas-covered" className="text-red-600 font-medium whitespace-nowrap cursor-pointer">Areas Covered</Link>
              <Link to="/about" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button className="text-gray-700 hover:text-red-600 cursor-pointer">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-red-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">Service Areas</h2>
          <p className="text-xl text-red-100 max-w-3xl mx-auto">
            Professional solar panel cleaning, window washing, and bird proofing services throughout Sacramento and surrounding communities. We're proud to serve homeowners and businesses across the greater Sacramento area.
          </p>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Cities We Serve</h3>
            <p className="text-xl text-gray-600">
              Click on your city to learn more about our solar panel cleaning, window washing, and bird proofing services in your area
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {serviceAreas.map((area) => (
              <Link
                key={area.slug}
                to={`/areas/${area.slug}`}
                className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                    <i className="ri-map-pin-line text-2xl text-sky-600"></i>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-gray-400 group-hover:text-red-600 transition-colors"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{area.name}</h4>
                <p className="text-sm text-gray-600">ZIP: {area.zip}</p>
                <p className="text-sm text-sky-600 mt-3 font-medium">View Services →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What We Offer in Every Area</h3>
            <p className="text-xl text-gray-600">
              Consistent quality solar panel cleaning, window washing, and bird proofing services across all locations
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-sun-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Solar Panel Cleaning</h4>
              <p className="text-gray-600">
                Professional solar panel cleaning using deionized water to maximize energy efficiency and extend panel lifespan for residential and commercial properties.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-window-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Window Washing</h4>
              <p className="text-gray-600">
                Crystal clear window cleaning for homes and businesses with professional streak-free results. Interior, exterior, screens, and track cleaning included.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Bird Proofing</h4>
              <p className="text-gray-600">
                Protect your solar panels and buildings from bird nesting and damage with professional mesh installation, spikes, and humane deterrent systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Serving Your Community</h3>
          <p className="text-xl text-red-100 mb-8">
            Don't see your city listed? Contact us! We may still service your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a 
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call: (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-sky-400 mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Mr. EcoClean
              </h4>
              <p className="text-gray-300 mb-4">
                Professional solar panel cleaning, window washing, and bird proofing services for Sacramento and surrounding areas.
              </p>
              <div className="flex space-x-4">
                <i className="ri-facebook-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-instagram-line text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-google-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Panel Cleaning</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Window Washing</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Bird Proofing</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">(916) 836-6145</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">Jason@mrecoclean.com</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Sacramento, CA
                </p>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Mr. EcoClean. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-sky-400 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
